if (!customElements.get('load-more-pagination')) {
  class LoadMorePagination extends HTMLElement {
    constructor() {
      super();
      this.link = this.querySelector('.load-more-pagination__link');
      this.nextCountEl = this.querySelector('.load-more-pagination__next-count');
      this.loadingEl = this.querySelector('.load-more-pagination__loading');

      this.sectionId = this.dataset.sectionId;
      this.currentPage = parseInt(this.dataset.currentPage, 10);
      this.totalPages = parseInt(this.dataset.totalPages, 10);
      this.pageSize = parseInt(this.dataset.pageSize, 10);
      this.mobilePageSize = parseInt(this.dataset.mobilePageSize, 10) || this.pageSize;
      this.totalResults = parseInt(this.dataset.totalResults, 10);
      this.nextUrl = this.dataset.nextUrl;

      this.staticMode = this.dataset.staticMode === 'true';
      this.gridSelector = this.dataset.gridSelector || '#product-grid';
      this.itemSelector =
        this.dataset.itemSelector || '.collection-grid__item:not(.collection-grid__item--promo-banner)';

      this.isLoading = false;
      this.productGrid = document.querySelector(this.gridSelector);
      this.mobileMediaQuery = window.matchMedia('(max-width: 749px)');
      this.usesMobileHide = Boolean(
        document.querySelector(
          '.collection--culture-event .collection-product-grid, .collection--past-coucou-events .collection-product-grid, .collection--workshop-event .collection-product-grid',
        ),
      );

      this.init();
    }

    init() {
      if (this.link) {
        this.link.addEventListener('click', this.handleLoadMore.bind(this));
      }
      if (this.usesMobileHide) {
        this.applyViewportCount();
        this.mobileMediaQuery.addEventListener('change', () => this.applyViewportCount());
      }
    }

    applyViewportCount() {
      if (!this.nextCountEl) return;
      const isMobile = this.mobileMediaQuery.matches;
      const desktopCount = parseInt(this.nextCountEl.dataset.desktopCount, 10);
      const mobileCount = parseInt(this.nextCountEl.dataset.mobileCount, 10);
      this.nextCountEl.textContent = isMobile ? mobileCount : desktopCount;
    }

    async handleLoadMore(event) {
      event.preventDefault();

      if (this.isLoading) return;

      if (this.staticMode) {
        this.handleStaticLoadMore();
        return;
      }

      const isMobileReveal = this.usesMobileHide && this.mobileMediaQuery.matches;

      if (isMobileReveal) {
        // Reveal up to mobilePageSize items. Fetch additional server pages on demand
        // when not enough hidden items are available. New fetched items start hidden
        // on mobile so we can reveal exactly mobilePageSize regardless of server batch size.
        this.setLoadingState(true);
        try {
          let toReveal = this.mobilePageSize;
          while (toReveal > 0) {
            const hidden = this.productGrid.querySelectorAll('.collection-grid__item--mobile-hidden');
            const revealNow = Math.min(hidden.length, toReveal);
            for (let i = 0; i < revealNow; i++) {
              hidden[i].classList.remove('collection-grid__item--mobile-hidden');
            }
            toReveal -= revealNow;
            if (toReveal === 0) break;
            if (!this.nextUrl) break;
            // A page can come back with nothing in it — every product on it was
            // past its last date — so an empty fetch is only a reason to stop
            // once there are no pages left behind it.
            const fetched = await this.fetchNextPage({ hideNewItems: true });
            if (!fetched && !this.nextUrl) break;
          }
          this.updateMobileRevealState();
          this.triggerAnimations();
        } catch (error) {
          console.error('Error loading more products:', error);
        } finally {
          this.setLoadingState(false);
        }
        return;
      }

      if (!this.nextUrl) return;

      this.setLoadingState(true);
      try {
        // Same reason as the mobile branch: keep going until the click has
        // actually put something on the page, rather than appending a page
        // that filtered down to nothing and looking broken.
        let appended = 0;
        while (this.nextUrl && appended === 0) {
          appended = await this.fetchNextPage();
        }
        this.triggerAnimations();
      } catch (error) {
        console.error('Error loading more products:', error);
      } finally {
        this.setLoadingState(false);
      }
    }

    // Returns how many items were appended, so callers can tell a page that
    // filtered down to nothing from one that genuinely ended the list.
    async fetchNextPage({ hideNewItems = false } = {}) {
      if (!this.nextUrl) return 0;

      const separator = this.nextUrl.includes('?') ? '&' : '?';
      const fetchUrl = `${this.nextUrl}${separator}section_id=${this.sectionId}`;

      const response = await fetch(fetchUrl, { headers: { Accept: 'text/html' } });
      if (!response.ok) throw new Error('Network response was not ok');

      const html = await response.text();
      const parser = new DOMParser();
      const doc = parser.parseFromString(html, 'text/html');

      let appended = 0;
      const newProductGrid = doc.querySelector(this.gridSelector);
      if (newProductGrid && this.productGrid) {
        const newItems = newProductGrid.querySelectorAll(this.itemSelector);
        appended = newItems.length;
        newItems.forEach((item) => {
          const clone = item.cloneNode(true);
          clone.querySelectorAll('.scroll-trigger').forEach((el) => {
            el.classList.add('scroll-trigger--cancel');
          });
          if (clone.classList.contains('scroll-trigger')) {
            clone.classList.add('scroll-trigger--cancel');
          }
          if (hideNewItems) {
            clone.classList.add('collection-grid__item--mobile-hidden');
          }
          this.productGrid.appendChild(clone);
        });
      }

      this.currentPage++;
      this.dataset.currentPage = this.currentPage;

      // Get the new pagination element from response
      const newPagination = doc.querySelector('load-more-pagination');
      if (newPagination) {
        this.nextUrl = newPagination.dataset.nextUrl || null;
        this.dataset.nextUrl = this.nextUrl || '';

        if (this.link && this.nextUrl) {
          this.link.href = this.nextUrl;
        }

        if (this.nextUrl) {
          const loadedCount = this.currentPage * this.pageSize;
          const remaining = this.totalResults - loadedCount;
          const nextCount = Math.min(this.pageSize, remaining);

          if (this.nextCountEl) {
            this.nextCountEl.dataset.desktopCount = nextCount;
            this.nextCountEl.textContent = nextCount;
          }
        } else {
          this.showEndMessage();
        }
      } else {
        this.showEndMessage();
      }

      if (this.usesMobileHide) {
        this.applyViewportCount();
      }

      return appended;
    }

    handleStaticLoadMore() {
      const grid = document.querySelector(this.gridSelector);
      if (!grid) return;

      const hiddenItems = grid.querySelectorAll(`${this.itemSelector}[hidden]`);
      if (hiddenItems.length === 0) {
        this.showEndMessage();
        return;
      }

      const revealCount = Math.min(this.pageSize, hiddenItems.length);
      for (let i = 0; i < revealCount; i++) {
        hiddenItems[i].removeAttribute('hidden');
      }

      this.currentPage++;
      this.dataset.currentPage = this.currentPage;

      const remainingHidden = hiddenItems.length - revealCount;
      if (remainingHidden === 0) {
        this.showEndMessage();
      } else if (this.nextCountEl) {
        const nextCount = Math.min(this.pageSize, remainingHidden);
        this.nextCountEl.textContent = nextCount;
        this.nextCountEl.dataset.desktopCount = nextCount;
        this.nextCountEl.dataset.mobileCount = nextCount;
      }

      this.triggerAnimations();
    }

    // Mobile reveal with no further server page (the whole collection fits on
    // one page): after a tap, either show the end message once nothing is left
    // hidden, or shrink the label to what one more tap will actually reveal.
    updateMobileRevealState() {
      if (this.nextUrl) return;
      const left = this.productGrid
        ? this.productGrid.querySelectorAll('.collection-grid__item--mobile-hidden').length
        : 0;
      if (left === 0) {
        this.showEndMessage();
        return;
      }
      const nextCount = Math.min(this.mobilePageSize, left);
      if (this.nextCountEl) {
        this.nextCountEl.dataset.mobileCount = nextCount;
        this.nextCountEl.textContent = nextCount;
      }
    }

    setLoadingState(loading) {
      this.isLoading = loading;
      if (this.link) {
        this.link.setAttribute('aria-busy', loading.toString());
        if (this.loadingEl) {
          this.loadingEl.hidden = !loading;
        }
      }
    }

    showEndMessage() {
      const container = this.querySelector('.load-more-pagination__container');
      if (!container) return;

      // Count what is actually on the page rather than trusting the server's
      // total: the grid drops products whose last date has passed, and Liquid
      // cannot know how many of those sit on pages it never rendered. By the
      // time this runs every page has been fetched, so the DOM is the truth.
      const grid = document.querySelector(this.gridSelector);
      const shown = grid ? grid.querySelectorAll(this.itemSelector).length : this.totalResults;

      container.innerHTML = `
              <p class="load-more-pagination__end-message">
                SHOWING ALL ${shown} RESULTS
              </p>
            `;
    }

    triggerAnimations() {
      // Re-trigger scroll animations for newly added items
      if (typeof window.ScrollTrigger !== 'undefined') {
        window.ScrollTrigger.refresh();
      }

      // Dispatch event for other scripts that might need to initialize
      document.dispatchEvent(
        new CustomEvent('load-more:loaded', {
          detail: { page: this.currentPage },
        }),
      );
    }
  }

  customElements.define('load-more-pagination', LoadMorePagination);
}